import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Zap, Loader2, Megaphone, Camera } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { persist } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "creator", category: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const path = isLogin ? "/auth/login" : "/auth/register";
      const { data } = await api.post(path, form);
      persist(data);
      toast.success(isLogin ? "Welcome back!" : "Account created 🎉");
      nav("/feed");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDF8FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center shadow-lg shadow-pink-500/30">
            <Zap className="h-6 w-6 text-white" fill="white" />
          </div>
          <span className="font-display font-extrabold text-2xl">MrktMe</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 animate-fade-up">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-slate-500 text-sm mt-1">{isLogin ? "Log in to your collab hub." : "Join creators & brands building together."}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: "creator", label: "Creator", icon: Camera },
                    { v: "brand", label: "Brand", icon: Megaphone },
                  ].map((r) => {
                    const Icon = r.icon; const active = form.role === r.v;
                    return (
                      <button type="button" key={r.v} data-testid={`role-${r.v}`} onClick={() => setForm({ ...form, role: r.v })}
                        className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                          active ? "border-pink-500 bg-pink-50 text-pink-600" : "border-slate-200 text-slate-500 hover:border-pink-200"}`}>
                        <Icon className="h-5 w-5" /> {r.label}
                      </button>
                    );
                  })}
                </div>
                <input data-testid="auth-name" required placeholder="Full name" value={form.name} onChange={set("name")}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all" />
                {form.role === "brand" ? (
                  <input data-testid="auth-company" placeholder="Company name" value={form.company} onChange={set("company")}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all" />
                ) : (
                  <input data-testid="auth-category" placeholder="Niche (e.g. Beauty, Tech)" value={form.category} onChange={set("category")}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all" />
                )}
              </>
            )}
            <input data-testid="auth-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all" />
            <input data-testid="auth-password" required type="password" placeholder="Password" value={form.password} onChange={set("password")}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all" />

            {isLogin && (
              <div className="text-right -mt-1">
                <Link data-testid="auth-forgot" to="/forgot-password" className="text-sm font-semibold text-pink-600 hover:underline">Forgot password?</Link>
              </div>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-rose-600 font-medium">{error}</p>}

            <button data-testid="auth-submit" disabled={loading} type="submit"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-white brand-gradient shadow-lg shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60">
              {loading && <Loader2 className="h-5 w-5 animate-spin" />}
              {isLogin ? "Log in" : "Create account"}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {isLogin ? "New here? " : "Already a member? "}
            <Link data-testid="auth-switch" to={isLogin ? "/register" : "/login"} className="text-pink-600 font-bold hover:underline">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
